import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeOfType } from '@lexical/utils';
import {
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { EditorToolbar } from './editor-toolbar';
import { $createSectionDividerNode } from './section-divider-node';
import { $createSpeakerNoteNode } from './speaker-note-node';

interface FormatState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isBulletList: boolean;
  isNumberedList: boolean;
}

const INITIAL_STATE: FormatState = {
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isBulletList: false,
  isNumberedList: false,
};

export function ToolbarPlugin({ onExport }: { onExport?: () => void }) {
  const [editor] = useLexicalComposerContext();
  const [formatState, setFormatState] = useState<FormatState>(INITIAL_STATE);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    const isBold = selection.hasFormat('bold');
    const isItalic = selection.hasFormat('italic');
    const isUnderline = selection.hasFormat('underline');

    let isBulletList = false;
    let isNumberedList = false;

    const anchorNode = selection.anchor.getNode();
    const element =
      anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    const elementKey = element.getKey();
    const elementDOM = editor.getElementByKey(elementKey);

    if (elementDOM !== null) {
      const parentList = $getNearestNodeOfType(anchorNode, ListNode);
      if ($isListNode(parentList)) {
        const listType = parentList.getListType();
        isBulletList = listType === 'bullet';
        isNumberedList = listType === 'number';
      }
    }

    setFormatState({ isBold, isItalic, isUnderline, isBulletList, isNumberedList });
  }, [editor]);

  useEffect(() => {
    const unregisterCommand = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      1, // COMMAND_PRIORITY_LOW
    );

    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });

    return () => {
      unregisterCommand();
      unregisterUpdate();
    };
  }, [editor, updateToolbar]);

  const onBold = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor]);

  const onItalic = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor]);

  const onUnderline = useCallback(() => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  }, [editor]);

  const onBulletList = useCallback(() => {
    if (formatState.isBulletList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, formatState.isBulletList]);

  const onNumberedList = useCallback(() => {
    if (formatState.isNumberedList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  }, [editor, formatState.isNumberedList]);

  const onIndent = useCallback(() => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const onOutdent = useCallback(() => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  }, [editor]);

  const onInsertSectionBreak = useCallback(() => {
    editor.update(() => {
      $insertNodes([$createSectionDividerNode()]);
    });
  }, [editor]);

  const onInsertSpeakerNote = useCallback(() => {
    editor.update(() => {
      $insertNodes([$createSpeakerNoteNode('')]);
    });
  }, [editor]);

  return (
    <EditorToolbar
      formatState={formatState}
      onBold={onBold}
      onItalic={onItalic}
      onUnderline={onUnderline}
      onBulletList={onBulletList}
      onNumberedList={onNumberedList}
      onIndent={onIndent}
      onOutdent={onOutdent}
      onInsertSectionBreak={onInsertSectionBreak}
      onInsertSpeakerNote={onInsertSpeakerNote}
      onExport={onExport}
    />
  );
}
